import { Close, GroupOutlined, Person2Outlined, Search } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Card,
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import ksimConsole from '../assets/ksim-console.png';
import Fallback from '../fallback/Fallback';
import { User, UserGroup } from '../types';
import { useActiveCompany } from '../utils/useCompany';

export interface StudentsFormProps {
  onUpdate: (userIds: string[]) => void;
  simulatorId: string;
  value: string[];
}

type Selectable<T> = T & { selected: boolean; indeterminate: boolean };

const isUser = <U extends User, G extends UserGroup>(row: U | G): row is U => 'email' in row;

const StudentsFormFields = ({ onUpdate, simulatorId, value }: StudentsFormProps) => {
  const activeCompany = useActiveCompany();

  const {
    data: users,
    isLoading,
    isError,
  } = useQuery<User[]>({
    enabled: !!(activeCompany && simulatorId),
    queryKey: [
      {
        path: `teach/companies/${activeCompany?.companyId}/users?${new URLSearchParams({
          simulator: simulatorId,
        }).toString()}`,
      },
    ],
  });
  const { data: groups } = useQuery<UserGroup[]>({
    enabled: !!(activeCompany && simulatorId),
    queryKey: [
      {
        path: `teach/companies/${activeCompany?.companyId}/groups?${new URLSearchParams({
          simulator: simulatorId,
        }).toString()}`,
      },
    ],
  });

  const [search, setSearch] = useState<string>('');
  const searchFilter = row => row.name.toLowerCase().includes(search.toLowerCase());

  const participants = users || [];
  const filteredUsers = search.length ? participants.filter(searchFilter) : participants;
  const filteredGroups = search.length ? (groups ?? []).filter(searchFilter) : groups ?? [];

  const addSelectionProps = (row: User | UserGroup) => ({
    ...row,
    indeterminate:
      !isUser(row) && !row.userIds.every(id => value.includes(id)) && row.userIds.some(id => value.includes(id)), // selected userIds are partly in the list of userIds of the group
    selected: isUser(row) ? value.some(id => id == row.id) : row.userIds.every(id => value.includes(id)), // Select user, or select group if all of its userIds are selected
  });

  const rows = [...participants, ...(groups ?? [])].map(addSelectionProps);
  const filteredRows: Selectable<User | UserGroup>[] = [...filteredUsers, ...filteredGroups].map(addSelectionProps);

  return (
    <>
      <TextField
        label="Search"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
          endAdornment: search && (
            <InputAdornment position="end">
              <IconButton onClick={() => setSearch('')} size="small">
                <Close />
              </IconButton>
            </InputAdornment>
          ),
        }}
        name="nameFilter"
        onChange={e => setSearch(e.target.value)}
        value={search}
      />

      {filteredRows.length || isLoading || isError ? (
        <>
          <Card>
            <DataGrid
              keepNonExistentRowsSelected
              loading={isLoading}
              // @ts-ignore clickedRow is in the type defs but that is incorrect
              onRowClick={({ row: clickedRow }: { row: Selectable<User> | Selectable<UserGroup> }) => {
                const isClickedUser = isUser<Selectable<User>, Selectable<UserGroup>>(clickedRow);
                onUpdate(
                  rows
                    // Toggle user row, or
                    .filter(row => {
                      // No groupIds can be in selected state.
                      // Groups will display checked on rendering based on userIds in selected state, see const filteredRows above
                      if (!isUser(row)) {
                        return false;
                      }

                      if (isClickedUser) {
                        // Toggle user selection
                        return row.id === clickedRow.id ? !row.selected : row.selected;
                      } else {
                        // Toggle selected users of clicked group
                        return clickedRow.userIds.includes(row.id) ? !clickedRow.selected : row.selected;
                      }
                    })
                    .map(({ id }) => id),
                );
              }}
              columns={[
                {
                  field: 'name',
                  flex: 1,
                  renderCell: params => (
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                      <Avatar sx={{ height: 24, width: 24 }}>
                        {isUser(params.row) ? (
                          <Person2Outlined sx={{ height: 16, width: 16 }} />
                        ) : (
                          <GroupOutlined sx={{ height: 16, width: 16 }} />
                        )}
                      </Avatar>
                      <ListItemText
                        sx={{ flex: 1 }}
                        secondary={isUser(params.row) ? params.row.email : null}
                        primary={params.row.name}
                      />
                      {/* We have to manually render checkboxes because MUI DataGrid does not support indeterminate state */}
                      <Checkbox checked={params.row.selected} indeterminate={params.row.indeterminate} />
                    </Box>
                  ),
                  renderHeader: () => `Students (${filteredUsers.length}) groups (${filteredGroups.length})`,
                },
              ]}
              disableColumnFilter
              disableColumnMenu
              getRowId={row => row.id}
              rows={filteredRows}
              sx={{ height: 335 }}
            />
          </Card>
          {isError && <Alert severity="error">An issue prevented the list of students to be fetched</Alert>}
        </>
      ) : (
        <Fallback imgSrc={ksimConsole} message="There are no students to select from" sx={{ mt: 0 }} />
      )}

      <Typography color="text.secondary" variant="body2" sx={{ textAlign: 'right' }}>
        {value.length} student{value.length === 1 ? '' : 's'} selected
      </Typography>
    </>
  );
};

export default StudentsFormFields;
